"use client";

import { ContractRepo } from "@/core/repos/tables/contract.repo";
import { NotificationRepo } from "@/core/repos/tables/notification.repo";
import container from "@/lib/di/container";
import { useState, useEffect } from "react";
import { Button, Input, message, Modal } from "antd";
import { SendOutlined, MessageOutlined, LinkOutlined } from "@ant-design/icons";
import { ContractModel } from "@/core/models/contracts/contract.model";
import { formatDateTimePipe } from "@/core/pipes/format-date.pipe";
import { contractStatusPipe } from "@/core/pipes/contract-status.pipe";
import { WaterUserStatus, WaterUserDepositStatus } from "@/core/enums/global.enums";
import { depositStatusPipe } from "@/core/pipes/deposit-status.pipe";
import { formatCurrencyPipe } from "@/core/pipes/format-currency.pipe";
import {
  FilterInputTemplate,
  FilterInputType,
  handleAddressChange,
  FilterBar,
} from "@/app/home/components/filter/FilterBar";
import {
  DataTable,
  FilterSearchPayload,
} from "@/app/home/components/table/DataTable";
import { TollAreaRepo } from "@/core/repos/tables/toll-area.repo";
import { UnitTypeRepo } from "@/core/repos/tables/unit-type.repo";
import { AddressRepo } from "@/core/repos/address.repo";

const contractRepo = container.get(ContractRepo);
const notificationRepo = container.get(NotificationRepo);
const tollAreaRepo = container.get(TollAreaRepo);
const unitTypeRepo = container.get(UnitTypeRepo);
const addressRepo = container.get(AddressRepo);

const { TextArea } = Input;

export default function SendNotificationPage() {
  const [filterMap, setFilterMap] = useState<Map<string, FilterSearchPayload>>(
    new Map()
  );
  const [selectedRows, setSelectedRows] = useState<ContractModel[]>([]);
  const [allUserIds, setAllUserIds] = useState<string[]>([]);
  const [isSelectAllPages, setIsSelectAllPages] = useState(false);
  const [currentPageSize, setCurrentPageSize] = useState(10);
  const [isSendModalOpen, setSendModalOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [notificationTitle, setNotificationTitle] = useState("");
  const [notificationContent, setNotificationContent] = useState("");
  const [messageApi, contextHolder] = message.useMessage();
  const [filterTemplates, setFilterTemplates] = useState<FilterInputTemplate[]>(
    [
      {
        dataIndex: "search",
        label: "Tên/ Mã hợp đồng",
        placeholder: "Nhập Tên/ Mã hợp đồng",
        type: FilterInputType.text,
      },
      {
        dataIndex: "note",
        label: "Ghi chú",
        placeholder: "Nhập ghi chú",
        type: FilterInputType.text,
      },
      {
        dataIndex: "meterCode",
        label: "Mã đồng hồ",
        placeholder: "Nhập mã đồng hồ",
        type: FilterInputType.text,
      },
      {
        dataIndex: "phone",
        label: "Số điện thoại",
        placeholder: "Nhập số điện thoại",
        type: FilterInputType.text,
      },
      {
        dataIndex: "identityNo",
        label: "CCCD",
        placeholder: "Nhập CCCD",
        type: FilterInputType.text,
      },
      {
        dataIndex: "hasMeter",
        label: "Trạng thái lắp đồng hồ",
        placeholder: "Chọn Trạng thái lắp đồng hồ",
        type: FilterInputType.select,
        options: [
          {
            name: "Có đồng hồ",
            value: true,
          },
          {
            name: "Chưa có đồng hồ",
            value: false,
          },
        ],
      },
      {
        dataIndex: "status",
        label: "Trạng thái hoạt động",
        placeholder: "Chọn Trạng thái hoạt động",
        type: FilterInputType.select,
        options: [
          {
            name: contractStatusPipe(WaterUserStatus.Processing),
            value: WaterUserStatus.Processing,
          },
          {
            name: contractStatusPipe(WaterUserStatus.Active),
            value: WaterUserStatus.Active,
          },
          {
            name: contractStatusPipe(WaterUserStatus.TempDisabled),
            value: WaterUserStatus.TempDisabled,
          },
          {
            name: contractStatusPipe(WaterUserStatus.Disabled),
            value: WaterUserStatus.Disabled,
          },
        ],
      },
      {
        dataIndex: "tollAreaId",
        label: "Khu vực",
        placeholder: "Chọn Khu vực",
        type: FilterInputType.select,
        options: [],
      },
      {
        dataIndex: "unitTypeId",
        label: "Loại hình đơn vị",
        placeholder: "Chọn Loại hình đơn vị",
        type: FilterInputType.select,
        options: [],
      },
      {
        dataIndex: "provinceId",
        label: "Tỉnh/ TP",
        placeholder: "Chọn Tỉnh/ TP",
        type: FilterInputType.select,
        options: [],
      },
      {
        dataIndex: "districtId",
        label: "Quận/ Huyện",
        placeholder: "Chọn Quận/ Huyện",
        type: FilterInputType.select,
        options: [],
      },
      {
        dataIndex: "wardId",
        label: "Xã/ Phường",
        placeholder: "Chọn Xã/ Phường",
        type: FilterInputType.select,
        options: [],
      },
      {
        dataIndex: "depositStatus",
        label: "Trạng thái thu cọc",
        placeholder: "Chọn trạng thái thu cọc",
        type: FilterInputType.select,
        options: Object.keys(WaterUserDepositStatus).map((key) => ({name: depositStatusPipe(key), value: key})),
      },
    ]
  );

  useEffect(() => {
    init();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const populateSelections = async () => {
    const provinces = (await addressRepo.getAllProvinces()).map((data) => ({
      name: data[1].name,
      value: data[0],
    }));
    const tollAreas = (await tollAreaRepo.getAll()).map((area) => ({
      name: area.name,
      value: area.id,
    }));
    const unitTypes = (await unitTypeRepo.getAll()).map((unitType) => ({
      name: unitType.name,
      value: unitType.id,
    }));
    const templates: FilterInputTemplate[] = [...filterTemplates];
    const provinceTemplate = templates.find(
      (template) => template.dataIndex === "provinceId"
    );
    if (provinceTemplate) {
      provinceTemplate.options = provinces;
    }
    const tollAreaTemplate = templates.find(
      (template) => template.dataIndex === "tollAreaId"
    );
    if (tollAreaTemplate) {
      tollAreaTemplate.options = tollAreas;
    }
    const unitTypeTemplate = templates.find(
      (template) => template.dataIndex === "unitTypeId"
    );
    if (unitTypeTemplate) {
      unitTypeTemplate.options = unitTypes;
    }
    setFilterTemplates(templates);
  };

  const init = async () => {
    await populateSelections();
  };

  async function onFilterChange(
    map: Map<string, FilterSearchPayload>,
    changes: string[]
  ) {
    await handleAddressChange(
      map,
      changes,
      filterTemplates,
      setFilterTemplates
    );
    setFilterMap(new Map(map));
  }

  const handleSelectAllPages = async () => {
    try {
      // Build query params from filterMap
      const query: any = {};
      filterMap.forEach(filter => {
        if (filter.value) {
          query[filter.key] = filter.value;
        }
      });

      const ids = await contractRepo.getAllIds(query);
      setAllUserIds(ids);
      setIsSelectAllPages(true);
      messageApi.success(`Đã chọn tất cả ${ids.length} khách hàng từ tất cả các trang`);
    } catch (error) {
      messageApi.error("Có lỗi xảy ra khi tải danh sách khách hàng");
    }
  };

  function onRowSelect(rows: ContractModel[]) {
    setSelectedRows([...rows]);
    
    // Check if this is a "select all" action (when all rows on current page are selected)
    if (rows.length === currentPageSize && currentPageSize > 0) {
      // This means user clicked "select all" on current page
      // We want to select all pages instead
      handleSelectAllPages();
    } else if (rows.length === 0) {
      // User unselected all
      setIsSelectAllPages(false);
      setAllUserIds([]);
    } else if (rows.length < currentPageSize && isSelectAllPages) {
      // User manually selected some rows, disable select all pages mode
      setIsSelectAllPages(false);
      setAllUserIds([]);
    }
  }

  const onPagination = (current: number, pageSize: number) => {
    setCurrentPageSize(pageSize);
  };

  const showSendModal = () => {
    const selectedCount = isSelectAllPages ? allUserIds.length : selectedRows.length;
    if (selectedCount === 0) {
      messageApi.warning("Vui lòng chọn ít nhất một khách hàng để gửi thông báo");
      return;
    }
    setSendModalOpen(true);
  };

  const handleSendNotification = async () => {
    if (!notificationTitle.trim()) {
      messageApi.error("Vui lòng nhập tiêu đề thông báo");
      return;
    }
    if (!notificationContent.trim()) {
      messageApi.error("Vui lòng nhập nội dung thông báo");
      return;
    }

    setSending(true);
    try {
      // Use allUserIds if in select all pages mode, otherwise use selected rows
      const userIds = isSelectAllPages 
        ? allUserIds 
        : selectedRows.map((row) => row.id);
      
      const success = await notificationRepo.sendToMultipleUsers({
        title: notificationTitle,
        content: notificationContent,
        onlyAppNotification: true,
        userIds,
      });

      if (success) {
        messageApi.success(
          `Đã gửi thông báo thành công đến ${userIds.length} khách hàng`
        );
        setSendModalOpen(false);
        setNotificationTitle("");
        setNotificationContent("");
        setSelectedRows([]);
        setIsSelectAllPages(false);
        setAllUserIds([]);
      } else {
        messageApi.error("Gửi thông báo thất bại");
      }
    } catch (error) {
      messageApi.error("Có lỗi xảy ra khi gửi thông báo");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="px-4 py-6">
      {contextHolder}
      <FilterBar
        setMap={setFilterMap}
        onChange={onFilterChange}
        templates={filterTemplates}
        map={filterMap}
        actions={[
          <div key={1} className="col-span-4 flex gap-2">
            <Button
              type="primary"
              icon={<SendOutlined />}
              onClick={showSendModal}
              disabled={!isSelectAllPages && selectedRows.length === 0}
              style={{
                background:
                  (isSelectAllPages || selectedRows.length > 0) ? "var(--primary-button)" : "white",
              }}
            >
              Gửi thông báo ({isSelectAllPages ? allUserIds.length : selectedRows.length})
            </Button>
            <Button
              type="default"
              icon={<MessageOutlined />}
              onClick={() => {
                window.open("https://ads-new.vinaphone.com.vn/Agent/Home.aspx?module=Send_SMS_CC_VTT", "_blank");
              }}
            >
              Gửi SMS
            </Button>
            <Button
              type="default"
              icon={<LinkOutlined />}
              onClick={() => {
                window.open("https://oa.zalo.me/manage/oa?%3F", "_blank");
              }}
            >
              Gửi ZNS
            </Button>
          </div>,
        ]}
      />
      <div className="mt-4 border-t border-dashed border-gray-300 pt-6">
        <DataTable<ContractModel>
          columns={[
            { dataIndex: "code", name: "Mã HĐ" },
            { dataIndex: "name", name: "Đại diện HĐ" },
            { dataIndex: "organization", name: "Tên tổ chức" },
            { dataIndex: "userName", name: "TK đại diện" },
            { dataIndex: "tollAreaName", name: "Khu vực" },
            { dataIndex: "unitTypeName", name: "Loại hình đơn vị" },
            { dataIndex: "waterMeterCode", name: "Mã đồng hồ nước" },
            { dataIndex: "phone", name: "Số điện thoại" },
            { dataIndex: "address", name: "Địa chỉ" },
            {
              dataIndex: "depositAmount",
              name: "Số tiền đã nộp",
              pipe: formatCurrencyPipe,
            },
            {
              dataIndex: "lastReadDate",
              name: "Ngày đọc cuối",
              pipe: formatDateTimePipe,
            },
            { dataIndex: "lastWaterMeterNumber", name: "Chỉ số cuối" },
            { dataIndex: 'depositStatus', name: 'Trạng thái thu cọc', pipe: depositStatusPipe },
            {
              dataIndex: "status",
              name: "Trạng thái",
              pipe: contractStatusPipe,
            },
          ]}
          repository={contractRepo}
          filterMap={filterMap}
          onSelect={onRowSelect}
          onPagination={onPagination}
        ></DataTable>
      </div>

      <Modal
        title="Gửi thông báo"
        open={isSendModalOpen}
        onCancel={() => {
          setSendModalOpen(false);
          setNotificationTitle("");
          setNotificationContent("");
        }}
        footer={[
          <Button
            key="cancel"
            onClick={() => {
              setSendModalOpen(false);
              setNotificationTitle("");
              setNotificationContent("");
            }}
          >
            Hủy
          </Button>,
          <Button
            key="send"
            type="primary"
            loading={sending}
            onClick={handleSendNotification}
            icon={<SendOutlined />}
          >
            Gửi thông báo
          </Button>,
        ]}
        width={600}
      >
        <div className="py-4">
          <div className="mb-4">
            <p className="mb-2 font-medium">
              Số khách hàng được chọn: <span className="text-blue-600">
                {isSelectAllPages ? allUserIds.length : selectedRows.length}
              </span>
            </p>
            {isSelectAllPages && (
              <div className="mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                <p className="text-yellow-800 text-sm">
                  <strong>⚠️ Chú ý:</strong> Đang gửi thông báo đến tất cả khách hàng từ tất cả các trang ({allUserIds.length} khách hàng)
                </p>
              </div>
            )}
          </div>
          <div className="mb-4">
            <label className="block mb-2 font-medium">
              Tiêu đề thông báo <span className="text-red-500">*</span>
            </label>
            <Input
              placeholder="Nhập tiêu đề thông báo"
              value={notificationTitle}
              onChange={(e) => setNotificationTitle(e.target.value)}
              maxLength={200}
            />
          </div>
          <div className="mb-4">
            <label className="block mb-2 font-medium">
              Nội dung thông báo <span className="text-red-500">*</span>
            </label>
            <TextArea
              rows={6}
              placeholder="Nhập nội dung thông báo"
              value={notificationContent}
              onChange={(e) => setNotificationContent(e.target.value)}
              maxLength={1000}
              showCount
            />
          </div>
        </div>
      </Modal>
    </div>
  );
}

